import { useMemo } from "react";
import UtilizationExpenseDonut from "./UtilizationExpenseDonut";
import UtilizationBudgetInfoIcon from "./UtilizationBudgetInfoIcon";
import UtilizationExpenseInfoIcon from "./UtilizationExpenseInfoIcon";
import {
  computeCardTotals,
  formatShortMonthLabel,
  formatUtilizationAmount,
  getUtilizationPercentColor,
} from "./utilizationReportUtils";

const AmountCell = ({ value, className = "" }) => (
  <td className={`utilization-card__amount ${className}`.trim()}>
    {formatUtilizationAmount(value)}
  </td>
);

const BudgetAmountCell = ({
  value,
  blockType,
  budgetDetails,
  meta,
  className = "",
}) => (
  <td className={`utilization-card__amount ${className}`.trim()}>
    <UtilizationBudgetInfoIcon
      blockType={blockType}
      totalBudget={value}
      details={budgetDetails}
      meta={meta}
    >
      {formatUtilizationAmount(value)}
    </UtilizationBudgetInfoIcon>
  </td>
);

const ExpenseAmountCell = ({
  value,
  expenseType,
  expenseDetails,
  meta,
  className = "",
}) => (
  <td className={`utilization-card__amount ${className}`.trim()}>
    <UtilizationExpenseInfoIcon
      expenseType={expenseType}
      totalExpense={value}
      details={expenseDetails}
      meta={meta}
    >
      {formatUtilizationAmount(value)}
    </UtilizationExpenseInfoIcon>
  </td>
);

const SectionHeader = ({ lines, className = "" }) => (
  <span className={`utilization-card__section-head ${className}`.trim()}>
    {lines.map((line) => (
      <span key={line}>{line}</span>
    ))}
  </span>
);

const DonutCell = ({ expense, balance, percent }) => {
  const shouldHide = Number(expense) === 0 && Number(balance) === 0;
  const { tone, color, track } = getUtilizationPercentColor(percent);

  if (shouldHide) {
    return <td className="utilization-card__donut-cell utilization-card__donut-cell--empty" />;
  }

  return (
    <td
      className={`utilization-card__donut-cell utilization-card__donut-cell--tone-${tone}`}
      style={{ backgroundColor: track }}
    >
      <UtilizationExpenseDonut percent={percent} color={color} track={track} />
    </td>
  );
};

const UtilizationReportRow = ({ row, isTotal = false, meta }) => (
  <tr
    className={
      isTotal
        ? "utilization-card__total-row"
        : "utilization-card__data-row"
    }
  >
    <td className="utilization-card__sticky-col utilization-card__sno">
      {isTotal ? "" : row.slNo}
    </td>
    <td
      className={`utilization-card__sticky-col utilization-card__name${
        isTotal ? " utilization-card__total-label" : ""
      }`}
      title={row.rowLabel}
    >
      {row.rowLabel}
    </td>

    <BudgetAmountCell
      value={row.prevFy.totalBudget}
      blockType="prevFy"
      budgetDetails={row.prevFy.budgetDetails}
      meta={meta}
    />
    <ExpenseAmountCell
      value={row.prevFy.totalExpense}
      expenseType="prevFy"
      expenseDetails={row.prevFy.expenseDetails}
      meta={meta}
    />
    <AmountCell value={row.prevFy.totalBalance} />

    <BudgetAmountCell
      value={row.selectedFy.totalBudget}
      blockType="selectedFy"
      budgetDetails={row.selectedFy.budgetDetails}
      meta={meta}
    />
    <ExpenseAmountCell
      value={row.selectedFy.expenseUptoPrevMonth}
      expenseType="selectedUpto"
      expenseDetails={row.selectedFy.expenseDetails}
      meta={meta}
      className="utilization-card__col-month"
    />
    <ExpenseAmountCell
      value={row.selectedFy.expenseForMonth}
      expenseType="selectedForMonth"
      expenseDetails={row.selectedFy.expenseDetails}
      meta={meta}
      className="utilization-card__col-month"
    />
    <ExpenseAmountCell
      value={row.selectedFy.totalExpense}
      expenseType="selectedTotal"
      expenseDetails={row.selectedFy.expenseDetails}
      meta={meta}
    />
    <DonutCell
      expense={row.selectedFy.totalExpense}
      balance={row.selectedFy.totalBalance}
      percent={row.selectedFy.expensePercentOfBudget}
    />
    <AmountCell value={row.selectedFy.totalBalance} />

    <AmountCell value={row.cumulative.totalBudget} />
    <AmountCell value={row.cumulative.totalExpense} />
    <AmountCell value={row.cumulative.totalBalance} />
  </tr>
);

const UtilizationReportCard = ({ card, meta, variant = "default", totalRow: totalRowProp }) => {
  const isGrandTotal = variant === "grand";

  const totalRow = useMemo(() => {
    if (totalRowProp) return totalRowProp;
    return computeCardTotals(card.rows);
  }, [card.rows, totalRowProp]);

  const selectedSectionTitle = `For the month - ${meta.selectedMonthCaption || "-"} (FY ${meta.selectedFyLabel}) as of ${meta.asOnDate}`;
  const cumulativeSectionTitle = `Cumulative upto month - ${meta.selectedMonthCaption || "-"} (FY ${meta.selectedFyLabel}) as of ${meta.asOnDate}`;

  return (
    <div
      className={`utilization-card${
        isGrandTotal ? " utilization-card--grand-total" : ""
      }`}
    >
      <div className="utilization-card__title">
        {isGrandTotal ? "Grand Total" : card.title}
      </div>

      <div className="utilization-card__table-wrap">
        <table className="utilization-card__table">
          <colgroup>
            <col className="utilization-col utilization-col--sno" />
            <col className="utilization-col utilization-col--name" />
            <col span={3} className="utilization-col utilization-col--amount" />
            <col span={3} className="utilization-col utilization-col--amount" />
            <col className="utilization-col utilization-col--amount" />
            <col className="utilization-col utilization-col--donut" />
            <col className="utilization-col utilization-col--amount" />
            <col span={3} className="utilization-col utilization-col--amount" />
          </colgroup>
          <thead>
            <tr className="utilization-card__group-row">
              <th rowSpan={2} className="utilization-card__sticky-col utilization-card__group-sno">
                Sl
              </th>
              <th rowSpan={2} className="utilization-card__sticky-col utilization-card__group-name">
                {meta.rowColumnLabel}
              </th>
              <th colSpan={3} className="utilization-card__section utilization-card__section--prev">
                Upto previous FY ({meta.previousFyLabel})
              </th>
              <th colSpan={6} className="utilization-card__section utilization-card__section--selected">
                <SectionHeader lines={[selectedSectionTitle]} />
              </th>
              <th colSpan={3} className="utilization-card__section utilization-card__section--cumulative">
                <SectionHeader lines={[cumulativeSectionTitle]} />
              </th>
            </tr>
            <tr className="utilization-card__subhead-row">
              <th>Budget</th>
              <th>Expense</th>
              <th>Balance</th>

              <th>Budget</th>
              <th className="utilization-card__col-month">
                <SectionHeader
                  lines={[
                    "Expense upto",
                    formatShortMonthLabel(meta.previousMonthLabel),
                  ]}
                />
              </th>
              <th className="utilization-card__col-month">
                <SectionHeader
                  lines={[
                    "Expense for",
                    formatShortMonthLabel(meta.selectedMonthLabel),
                  ]}
                />
              </th>
              <th>Expense</th>
              <th className="utilization-card__col-donut">% Budget</th>
              <th>Balance</th>

              <th>Budget</th>
              <th>Expense</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            {totalRow && (
              <UtilizationReportRow row={totalRow} isTotal meta={meta} />
            )}
            {!isGrandTotal &&
              card.rows.map((row) => (
                <UtilizationReportRow
                  key={`${card.id}-${row.slNo}`}
                  row={row}
                  meta={meta}
                />
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UtilizationReportCard;
